import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { MakerFinance } from '../../../../types/estimate/common/PaymentWay';
import { installmentProcess } from '../../../../lib/estimate/common/paymentWay/calculator/installment';
import { Trim } from '../../../../types/common/car/CarInfo';
import { selectCarInfoState } from '../../../../data/estimate/common/selectCar/info/selector';
import { incidentalExpenseInfoState } from '../../../../data/estimate/common/incidentalExpense/info/selector';
import { consignmentPriceInfoState } from '../../../../data/estimate/common/consignment/info/selector';
import { exemptionOrTaxationInfoState } from '../../../../data/estimate/common/tax/info/selector';
import { Factory } from '../../../../types/estimate/new/ConsignmentInfo';
import { SelfInputForNumber } from '../../../../types/common/SelfInput';
import { paymentWayListState } from '../../../../data/estimate/common/paymentWay/list/selector';
import { paymentChargeInfoState } from '../../../../data/estimate/common/paymentCharge/info/selector';
import { otherBankInfoState } from '../../../../data/estimate/common/otherBank/info/selector';
import {
  otherBankConditionInfoState,
  otherBankAdvanceReceivedRateInfoState,
  otherBankAdvanceReceivedFullRateInfoState,
  otherBankInstallmentPrincipalInfoState,
  otherBankUntilRegistrationInfoState,
  otherBankUntilReleaseInfoState,
  otherBankDeliveryMoneyWithConsignmentPriceInfoState,
  otherBankDeliveryMoneyWithoutConsignmentPriceInfoState,
} from '../../../../data/estimate/common/otherBank/info/atom';
import useUpdateInfo from '../useUpdateInfo';
import useFetchOtherBank from '../fetch/useFetchOtherBank';

interface InputData {
  inputName: string; // 업데이트 key name
  inputValue: number; // 업데이트 value
}

/**
 *  메이커금융 선택시 기본값 세팅 함수를 리턴하는 hook
 *  */
function useCalculatorForOtherBankCondition(): {
  updateInstallment: (inputData: InputData) => void;
} {
  // selected
  // 트림
  const trim: Trim = useRecoilValue(selectCarInfoState({ name: 'trim' }));
  // 등록합계
  const registrationTotalPrice: number = useRecoilValue(
    incidentalExpenseInfoState({ name: 'registrationTotalPrice' })
  );
  // 합계(탁송료 제외)
  const totalPriceWithoutConsignmentPrice: number = useRecoilValue(
    exemptionOrTaxationInfoState({ name: 'totalPriceWithoutConsignmentPrice' })
  );
  // 출고장
  const factory: Factory = useRecoilValue(consignmentPriceInfoState({ name: 'factory' }));
  // 탁송료 직접 입력
  const selfConsignmentPrice: SelfInputForNumber = useRecoilValue(
    consignmentPriceInfoState({ name: 'selfConsignmentPrice' })
  );
  // 합계(탁송료 포함)
  const totalPriceWithConsignmentPrice: number = useRecoilValue(
    consignmentPriceInfoState({ name: 'totalPriceWithConsignmentPrice' })
  );
  // 세금계산서(인도금대납 반영전)
  const taxInvoice: number = useRecoilValue(consignmentPriceInfoState({ name: 'taxInvoice' }));

  // 메이커금융 상품 목록
  const productList: MakerFinance[] = useRecoilValue(
    paymentWayListState({ name: 'otherBankMakerFinance' })
  );

  // 메이커금융 상품
  const product: MakerFinance = useRecoilValue(otherBankConditionInfoState);

  // 기간 직접입력
  const selfPeriodInfo: SelfInputForNumber = useRecoilValue(
    otherBankInfoState({ name: 'otherBankSelfPeriod' })
  );

  // 기간
  const periodInfo: number = useRecoilValue(otherBankInfoState({ name: 'otherBankPeriod' }));

  // 선수율
  const advanceReceivedRateInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankAdvanceReceivedRate' })
  );
  // 선수율(소수점)
  const advanceReceivedFullRateInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankAdvanceReceivedFullRate' })
  );
  // 할부원금
  const installmentPrincipalInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankInstallmentPrincipal' })
  );

  // 등록까지
  const untilRegistrationInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankUntilRegistration' })
  );
  // 출고까지
  const untilReleaseInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankUntilRelease' })
  );
  // 인도+탁송
  const deliveryMoneyWithConsignmentPriceInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankDeliveryMoneyWithConsignmentPrice' })
  );
  // 인도-탁송
  const deliveryMoneyWithoutConsignmentPriceInfo: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankDeliveryMoneyWithoutConsignmentPrice' })
  );

  // 계약금
  const depositInfo: number = useRecoilValue(paymentChargeInfoState({ name: 'deposit' }));
  const selfDepositInfo: SelfInputForNumber = useRecoilValue(
    paymentChargeInfoState({ name: 'selfDeposit' })
  );
  const deposit: number = selfDepositInfo?.active ? selfDepositInfo.number : depositInfo;

  // 의무보험료
  const insuarancePremiumInfo: number = useRecoilValue(
    paymentChargeInfoState({ name: 'insuarancePremium' })
  );
  const selfInsuarancePremiumInfo: SelfInputForNumber = useRecoilValue(
    paymentChargeInfoState({ name: 'selfInsuarancePremium' })
  );
  const insuarancePremium: number = selfInsuarancePremiumInfo?.active
    ? selfInsuarancePremiumInfo.number
    : insuarancePremiumInfo;

  // update
  const updateAdvanceReceivedRate = useUpdateInfo(otherBankAdvanceReceivedRateInfoState); // 선수율
  const updateAdvanceReceivedFullRate = useUpdateInfo(otherBankAdvanceReceivedFullRateInfoState); // 선수율(소수점)
  const updateInstallmentPrincipal = useUpdateInfo(otherBankInstallmentPrincipalInfoState); // 할부원금
  const updateUntilRegistration = useUpdateInfo(otherBankUntilRegistrationInfoState); // 등록까지
  const updateuntilRelease = useUpdateInfo(otherBankUntilReleaseInfoState); // 출고까지
  const updatedeliveryPriceWithConsignmentPrice = useUpdateInfo(
    otherBankDeliveryMoneyWithConsignmentPriceInfoState
  ); // 인도+탁송
  const updatedeliveryPriceWithoutConsignmentPrice = useUpdateInfo(
    otherBankDeliveryMoneyWithoutConsignmentPriceInfoState
  ); // 인도-탁송

  // function
  const { updateOtherBankList } = useFetchOtherBank();

  /**
   *  원금 로우에서 값은 변경할 때 계산되는 함수
   * @param {InputData} inputData 변경된 값
   * 선수율(advanceReceivedRate), 할부원금(installmentPrincipal), 가용금액(availableAmount), 등록까지(untilRegistration), 출고까지(untilRelease), 인도+탁송(deliveryMoneyWithConsignmentPrice), 인도-탁송(deliveryMoneyWithoutConsignmentPrice)
   *  */
  const updateInstallment = useCallback(
    ({ inputName, inputValue }: InputData): void => {
      // 적용될 기간
      const period: number = !!selfPeriodInfo?.active ? selfPeriodInfo.number : periodInfo;

      // 적용될 탁송료
      const consignmentPrice: number = !!selfConsignmentPrice?.active
        ? selfConsignmentPrice.number
        : factory?.price || 0;

      // 적용될 선수율
      const applyAdvanceReceivedRate: number =
        inputName === 'advanceReceivedRate' ? inputValue : advanceReceivedRateInfo; // 선수율

      const {
        installmentPrincipal,
        advanceReceivedRate,
        advanceReceivedFullRate,
        deliveryMoneyWithConsignmentPrice,
        deliveryMoneyWithoutConsignmentPrice,
        untilRelease,
        untilRegistration,
      } = installmentProcess({
        taxInvoice,
        totalPriceWithoutConsignmentPrice,
        totalPriceWithConsignmentPrice,
        consignmentPrice,
        registrationTotalPrice,
        period,
        advanceReceivedRate: applyAdvanceReceivedRate,
        deposit,
        insuarancePremium,
        trim,
        product,
        productList,
        inputData: {
          name: inputName,
          value: inputValue,
        },
      });

      const correctAdvanceReceivedRate: number =
        advanceReceivedRate > 120 ? 120 : advanceReceivedRate < 0 ? 0 : advanceReceivedRate;

      updateOtherBankList({
        inputName: 'advanceReceivedRate',
        inputValue: correctAdvanceReceivedRate,
      });

      updateAdvanceReceivedRate(correctAdvanceReceivedRate); // 선수율
      updateAdvanceReceivedFullRate(advanceReceivedFullRate); // 선수율 소수점
      updateInstallmentPrincipal(installmentPrincipal); // 할부원금
      updateUntilRegistration(untilRegistration); // 등록까지
      updateuntilRelease(untilRelease); // 출고까지
      updatedeliveryPriceWithConsignmentPrice(deliveryMoneyWithConsignmentPrice); // 인도+탁송
      updatedeliveryPriceWithoutConsignmentPrice(deliveryMoneyWithoutConsignmentPrice); // 인도-탁송
    },
    [
      taxInvoice,
      totalPriceWithoutConsignmentPrice,
      totalPriceWithConsignmentPrice,
      selfConsignmentPrice,
      factory,
      registrationTotalPrice,
      selfPeriodInfo,
      periodInfo,
      advanceReceivedRateInfo,
      advanceReceivedFullRateInfo,
      installmentPrincipalInfo,
      untilRegistrationInfo,
      untilReleaseInfo,
      deliveryMoneyWithConsignmentPriceInfo,
      deliveryMoneyWithoutConsignmentPriceInfo,
      deposit,
      insuarancePremium,
    ]
  );

  return {
    updateInstallment,
  };
}

export default useCalculatorForOtherBankCondition;
